import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icone } from '../../design/Icone';

/**
 * Gaveta lateral do módulo Encomendas (encomendar, detalhe da encomenda, resolver). Não sai da
 * página: Esc ou o fundo fecham; o foco vai para a gaveta ao abrir. No celular ocupa a tela.
 */
export function Gaveta({
  titulo,
  subtitulo,
  aoFechar,
  rodape,
  largura = 640,
  children,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  aoFechar: () => void;
  rodape?: ReactNode;
  largura?: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', tecla);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', tecla);
      document.body.style.overflow = '';
      anterior?.focus();
    };
  }, [aoFechar]);
  return (
    <div
      className="enc-gaveta-fundo"
      onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}
    >
      <div
        ref={ref}
        className="enc-gaveta"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        style={{ width: `min(${String(largura)}px, 100%)` }}
      >
        <header className="enc-gaveta-cabeca">
          <div>
            <h2>{titulo}</h2>
            {subtitulo && <div className="enc-gaveta-sub">{subtitulo}</div>}
          </div>
          <button
            type="button"
            className="enc-gaveta-fechar"
            onClick={aoFechar}
            aria-label="Fechar"
          >
            <Icone nome="x" tamanho={18} traco={1.8} />
          </button>
        </header>
        <div className="enc-gaveta-corpo">{children}</div>
        {rodape && <footer className="enc-gaveta-rodape">{rodape}</footer>}
      </div>
    </div>
  );
}

/** Grupo de opções em "pílulas" (radio acessível). */
export function Opcoes<T extends string>({
  valor,
  opcoes,
  aoMudar,
  nome,
}: {
  valor: T;
  opcoes: readonly (readonly [T, string])[];
  aoMudar: (v: T) => void;
  nome: string;
}) {
  return (
    <div className="enc-opcoes" role="radiogroup" aria-label={nome}>
      {opcoes.map(([v, rotulo]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={valor === v}
          className={valor === v ? 'enc-opcao-sel' : ''}
          onClick={() => aoMudar(v)}
        >
          {rotulo}
        </button>
      ))}
    </div>
  );
}

export function Campo({
  rotulo,
  dica,
  erro,
  children,
  largo,
}: {
  rotulo: string;
  dica?: string | undefined;
  erro?: string | null;
  children: ReactNode;
  largo?: boolean;
}) {
  return (
    <label
      className={`enc-campo${largo ? ' enc-campo-largo' : ''}${erro ? ' enc-campo-erro' : ''}`}
    >
      <span className="enc-campo-rotulo">{rotulo}</span>
      {children}
      {erro ? (
        <span className="enc-campo-msg">{erro}</span>
      ) : dica ? (
        <span className="enc-campo-dica">{dica}</span>
      ) : null}
    </label>
  );
}
